import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useAppStore } from '../store/useAppStore';
import { apiClient } from '../api/client';
import { Card } from '../components/Card';
import { Header } from '../components/Header';
import { Icon } from '../components/Icon';
import { formatCurrency, stripEmojis } from '../utils/format';
import { useTranslation } from 'react-i18next';
import { PremiumFeature } from '../components/PremiumFeature';
import { useWebPowerSyncTransactions } from '../hooks/usePowerSync';
import './AnalyticsScreen.css';
 
type PeriodType = 'today' | 'week' | 'month' | 'year';

type CategoryViewType = 'list' | 'pie' | 'bar';

export const AnalyticsScreen: React.FC = () => {
  const { transactions: storeTransactions, settings } = useAppStore();
  const { t } = useTranslation();
  
  // PowerSync данные
  const { transactions: psTransactions } = useWebPowerSyncTransactions();
  const transactions = psTransactions.length > 0 ? psTransactions : storeTransactions;
  const [period, setPeriod] = useState<PeriodType>('month');
  const [insights, setInsights] = useState<any[]>([]);
  const [selectedInsight, setSelectedInsight] = useState<any>(null);
  const [categoryExpenses, setCategoryExpenses] = useState<any[]>([]);
  const [categoryView, setCategoryView] = useState<CategoryViewType>('list');
  const [loading, setLoading] = useState(false);
  const [selectedPieSlice, setSelectedPieSlice] = useState<string | null>(null);
  const carouselRef = useRef<HTMLDivElement>(null);
  
  // Дополнительные параметры выбора периода
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1); // 1-12
  const [selectedWeekOffset, setSelectedWeekOffset] = useState(0); // 0 = текущая неделя, -1 = прошлая и т.д.
  const [selectedDayOffset, setSelectedDayOffset] = useState(0); // 0 = сегодня, -1 = вчера и т.д.
  const [customDate, setCustomDate] = useState<string>(''); // YYYY-MM-DD формат для выбранной даты

  const loadAnalytics = useCallback(async () => {
    try {
      setLoading(true);
      console.log('📊 [Analytics] Loading analytics for period:', period, {
        selectedDayOffset,
        selectedWeekOffset,
        selectedYear,
        selectedMonth,
        customDate
      });
      
      // Функция для форматирования даты в локальный формат YYYY-MM-DD
      const formatLocalDate = (date: Date): string => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      };
      
      // Формируем параметры запроса в зависимости от выбранного периода
      let periodParam = period;
      let startDate, endDate;
      
      if (period === 'year') {
        // Для года: с 1 января по 31 декабря выбранного года
        const start = new Date(selectedYear, 0, 1);
        const end = new Date(selectedYear, 11, 31);
        startDate = formatLocalDate(start);
        endDate = formatLocalDate(end);
        periodParam = 'custom';
      } else if (period === 'month') {
        // Для месяца: с 1 числа по последнее число выбранного месяца и года
        const start = new Date(selectedYear, selectedMonth - 1, 1);
        const end = new Date(selectedYear, selectedMonth, 0);
        startDate = formatLocalDate(start);
        endDate = formatLocalDate(end);
        periodParam = 'custom';
      } else if (period === 'week') {
        // Для недели: вычисляем начало и конец недели с учетом смещения
        const today = new Date();
        const dayOfWeek = today.getDay();
        const diff = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
        
        const startOfWeek = new Date(today);
        startOfWeek.setDate(today.getDate() - diff + (selectedWeekOffset * 7));
        
        const endOfWeek = new Date(startOfWeek);
        endOfWeek.setDate(startOfWeek.getDate() + 6);
        
        startDate = formatLocalDate(startOfWeek);
        endDate = formatLocalDate(endOfWeek);
        periodParam = 'custom';
      } else if (period === 'today') {
        // Для дня: выбранный день с учетом смещения или произвольная дата
        let targetDate: Date;
        if (customDate) {
          // Если установлена произвольная дата, используем её
          targetDate = new Date(customDate + 'T00:00:00');
        } else {
          // Иначе считаем от текущего дня с учётом смещения
          const now = new Date();
          targetDate = new Date(now);
          targetDate.setDate(now.getDate() + selectedDayOffset);
        }
        startDate = formatLocalDate(targetDate);
        endDate = formatLocalDate(targetDate);
        periodParam = 'custom';
        console.log('📅 [Analytics] Day period:', { startDate, endDate, targetDate: targetDate.toISOString() });
      }
      
      console.log('📊 [Analytics] Final params:', { periodParam, startDate, endDate });
      
      // Загружаем инсайты (используем рассчитанные даты для custom периодов)
      try {
        console.log('📊 [Analytics] Loading insights...');
        if (periodParam === 'custom' && startDate && endDate) {
          const insightsData = await apiClient.getInsights(periodParam, startDate, endDate);
          console.log('✅ [Analytics] Insights loaded:', insightsData.insights?.length || 0);
          setInsights(insightsData.insights || []);
        } else {
          const insightsData = await apiClient.getInsights(period);
          console.log('✅ [Analytics] Insights loaded:', insightsData.insights?.length || 0);
          setInsights(insightsData.insights || []);
        }
      } catch (error: any) {
        console.error('❌ [Analytics] Error loading insights:', error.message || error);
        setInsights([]);
      }
      
      // Загружаем расходы по категориям (используем рассчитанные даты для custom периодов)
      try {
        console.log('📊 [Analytics] Loading category expenses with params:', { periodParam, startDate, endDate, originalPeriod: period });
        if (periodParam === 'custom' && startDate && endDate) {
          console.log('📊 [Analytics] API call: getCategoryExpenses(', periodParam, ',', startDate, ',', endDate, ')');
          const expensesData = await apiClient.getCategoryExpenses(periodParam, startDate, endDate);
          console.log('✅ [Analytics] Category expenses loaded:', expensesData.expenses?.length || 0, 'categories');
          console.log('✅ [Analytics] Category expenses data:', expensesData.expenses);
          setCategoryExpenses(expensesData.expenses || []);
        } else {
          console.log('📊 [Analytics] API call: getCategoryExpenses(', period, ')');
          const expensesData = await apiClient.getCategoryExpenses(period);
          console.log('✅ [Analytics] Category expenses loaded:', expensesData.expenses?.length || 0, 'categories');
          console.log('✅ [Analytics] Category expenses data:', expensesData.expenses);
          setCategoryExpenses(expensesData.expenses || []);
        }
      } catch (error: any) {
        console.error('❌ [Analytics] Error loading category expenses:', error.message || error);
        setCategoryExpenses([]);
      }
      
    } catch (error: any) {
      console.error('❌ [Analytics] General error:', error.message || error);
      console.error('❌ [Analytics] Stack:', error.stack);
    } finally {
      console.log('✅ [Analytics] Loading complete');
      setLoading(false);
    }
  }, [period, selectedYear, selectedMonth, selectedWeekOffset, selectedDayOffset, customDate]);

  useEffect(() => {
    console.log('🔄 [Analytics] Period changed, reloading data...');
    loadAnalytics();
    setSelectedPieSlice(null); // Сбрасываем выделение при изменении периода
  }, [period, selectedYear, selectedMonth, selectedWeekOffset, selectedDayOffset, customDate]);

  // Отключили автоперелистывание: оставляем только ручной свайп/скролл
  // Карусель прокручивается пользователем нативно (touch/scroll) без таймеров

  // Фильтрация транзакций с учетом периода и фильтра по типу
  const getFilteredTransactions = () => {
    // Функция для получения диапазона дат на основе выбранного периода
    const getDateRange = () => {
      if (period === 'today') {
        let targetDate: Date;
        if (customDate) {
          // Если установлена произвольная дата, используем её
          targetDate = new Date(customDate + 'T00:00:00');
        } else {
          // Иначе считаем от текущего дня с учётом смещения
          const now = new Date();
          targetDate = new Date(now);
          targetDate.setDate(now.getDate() + selectedDayOffset);
        }
        targetDate.setHours(0, 0, 0, 0);
        const end = new Date(targetDate);
        end.setHours(23, 59, 59, 999);
        return { start: targetDate, end };
      } else if (period === 'week') {
        const today = new Date();
        const dayOfWeek = today.getDay();
        const diff = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
        
        const startOfWeek = new Date(today);
        startOfWeek.setDate(today.getDate() - diff + (selectedWeekOffset * 7));
        startOfWeek.setHours(0, 0, 0, 0);
        
        const endOfWeek = new Date(startOfWeek);
        endOfWeek.setDate(startOfWeek.getDate() + 6);
        endOfWeek.setHours(23, 59, 59, 999);
        
        return { start: startOfWeek, end: endOfWeek };
      } else if (period === 'month') {
        const start = new Date(selectedYear, selectedMonth - 1, 1);
        start.setHours(0, 0, 0, 0);
        const end = new Date(selectedYear, selectedMonth, 0);
        end.setHours(23, 59, 59, 999);
        return { start, end };
      } else if (period === 'year') {
        const start = new Date(selectedYear, 0, 1);
        start.setHours(0, 0, 0, 0);
        const end = new Date(selectedYear, 11, 31);
        end.setHours(23, 59, 59, 999);
        return { start, end };
      }
      return { start: new Date(0), end: new Date() };
    };

    const { start, end } = getDateRange();
    
    const filtered = transactions.filter(t => {
      const date = new Date(t.date);
      return date >= start && date <= end;
    });
    
    return filtered;
  };

  // Подсчет метрик
  const calculateMetrics = () => {
    const periodTransactions = getFilteredTransactions();
    console.log('📊 [Metrics] Calculating for period:', period, {
      totalTransactions: transactions.length,
      periodTransactions: periodTransactions.length,
      period
    });

    const income = periodTransactions.filter(t => t.type === 'income').reduce((sum, t) => sum + t.amount, 0);
    const expense = periodTransactions.filter(t => t.type === 'expense').reduce((sum, t) => sum + t.amount, 0);
    const balance = income - expense;
    
    let daysInPeriod = 1;
    if (period === 'today') daysInPeriod = 1;
    else if (period === 'week') daysInPeriod = 7;
    else if (period === 'month') daysInPeriod = new Date(selectedYear, selectedMonth, 0).getDate();
    else if (period === 'year') daysInPeriod = 365;
    
    const avgDailyExpense = expense / daysInPeriod;
    const transactionsCount = periodTransactions.length;
    
    // Расходы по дням для графика
    const expensesByDay = new Map<string, number>();
    periodTransactions
      .filter(t => t.type === 'expense')
      .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()) // Сортировка от ранних к поздним
      .forEach(t => {
        const day = new Date(t.date).toLocaleDateString('ru-RU', { day: 'numeric', month: 'short' });
        expensesByDay.set(day, (expensesByDay.get(day) || 0) + t.amount);
      });

    return { income, expense, balance, avgDailyExpense, transactionsCount, expensesByDay };
  };

  // Локальный подсчет расходов по категориям (резервный вариант)
  const calculateLocalCategoryExpenses = () => {
    const periodTransactions = getFilteredTransactions();
    const expenseTransactions = periodTransactions.filter(t => t.type === 'expense');
    
    // Группируем по категориям
    const categoryMap = new Map<string, number>();
    expenseTransactions.forEach(t => {
      const category = t.category || 'Разное';
      categoryMap.set(category, (categoryMap.get(category) || 0) + t.amount);
    });
    
    // Вычисляем общие расходы
    const totalExpense = Array.from(categoryMap.values()).reduce((sum, amount) => sum + amount, 0);
    
    // Преобразуем в массив с процентами
    const expenses = Array.from(categoryMap.entries())
      .map(([category, amount]) => ({
        category,
        amount,
        percentage: totalExpense > 0 ? (amount / totalExpense) * 100 : 0
      }))
      .sort((a, b) => b.amount - a.amount);
    
    console.log('📊 [Local] Calculated category expenses:', expenses);
    return expenses;
  };

  const metrics = calculateMetrics();
  
  // Если categoryExpenses пустой (API не сработал), используем локальные данные
  const displayCategoryExpenses = categoryExpenses.length > 0 
    ? categoryExpenses 
    : calculateLocalCategoryExpenses();

  const periods = [
    { id: 'today' as PeriodType, label: t('transactions.today') },
    { id: 'week' as PeriodType, label: t('analytics.periods.week') || t('transactions.week') },
    { id: 'month' as PeriodType, label: t('analytics.periods.month') || t('transactions.month') },
    { id: 'year' as PeriodType, label: t('analytics.periods.year') || t('transactions.year') },
  ];

  return (
    <PremiumFeature featureId="analytics">
      <div className="analytics-screen">
      <Header title={t('analytics.title') || 'Аналитика'} />
      
      {/* Insights Carousel */}
      {insights.length > 0 && (
        <div className="section">
          <div className="insights-carousel-container">
            <div className="insights-carousel" ref={carouselRef}>
              {insights.map((insight, index) => (
                <div
                  key={index}
                  className="insight-card neo-raised neo-static"
                  onClick={() => setSelectedInsight(insight)}
                >
                  <div className="insight-card__icon">
                    <Icon name="lightbulb" size={20} />
                  </div>
                  <div className="insight-card__text">
                    {(insight.text || insight.message || '').substring(0, 80)}
                    {(insight.text || insight.message || '').length > 80 && '...'}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Period Selector */}
      <div className="section">
        <div className="period-selector">
          {periods.map((p) => (
            <button
              key={p.id}
              className={`period-selector__item ${period === p.id ? 'period-selector__item--active neo-raised neo-static' : ''}`}
              onClick={() => {
                setPeriod(p.id);
                // Сброс на текущий период при смене типа
                if (p.id === 'today') {
                  setSelectedDayOffset(0);
                  setCustomDate('');
                }
                if (p.id === 'week') setSelectedWeekOffset(0);
                if (p.id === 'month' || p.id === 'year') {
                  setSelectedYear(new Date().getFullYear());
                  setSelectedMonth(new Date().getMonth() + 1);
                }
              }}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {/* Day Selector */}
      {period === 'today' && (
        <div className="section">
          <div className="day-selector">
            <button 
              className="day-nav-btn neo-raised"
              onClick={() => {
                setSelectedDayOffset(selectedDayOffset - 1);
                setCustomDate('');
              }}
            >
              <Icon name="chevron-left" size={20} />
            </button>
            <div className="day-content">
              <span className="day-label">
                {customDate ? (
                  new Date(customDate + 'T00:00:00').toLocaleDateString('ru-RU', {
                    day: 'numeric',
                    month: 'long',
                    year: 'numeric'
                  })
                ) : selectedDayOffset === 0 ? (
                  'Сегодня'
                ) : selectedDayOffset === -1 ? (
                  'Вчера'
                ) : selectedDayOffset === -2 ? (
                  'Позавчера'
                ) : selectedDayOffset < 0 ? (
                  `${Math.abs(selectedDayOffset)} дней назад`
                ) : selectedDayOffset === 1 ? (
                  'Завтра'
                ) : (
                  `Через ${selectedDayOffset} дней`
                )}
              </span>
              <input 
                type="date"
                className="day-date-picker"
                value={customDate || (() => {
                  const d = new Date();
                  d.setDate(d.getDate() + selectedDayOffset);
                  return d.toISOString().split('T')[0];
                })()}
                onChange={(e) => {
                  setCustomDate(e.target.value);
                  setSelectedDayOffset(0);
                }}
              />
            </div>
            <button 
              className="day-nav-btn neo-raised"
              onClick={() => {
                setSelectedDayOffset(selectedDayOffset + 1);
                setCustomDate('');
              }}
            >
              <Icon name="chevron-right" size={20} />
            </button>
          </div>
        </div>
      )}

      {/* Week Selector */}
      {period === 'week' && (
        <div className="section">
          <div className="week-selector">
            <button 
              className="week-nav-btn neo-raised"
              onClick={() => setSelectedWeekOffset(selectedWeekOffset - 1)}
            >
              <Icon name="chevron-left" size={20} />
            </button>
            <span className="week-label">
              {selectedWeekOffset === 0 && 'Текущая неделя'}
              {selectedWeekOffset === -1 && 'Прошлая неделя'}
              {selectedWeekOffset < -1 && `${Math.abs(selectedWeekOffset)} недели назад`}
              {selectedWeekOffset === 1 && 'Следующая неделя'}
              {selectedWeekOffset > 1 && `Через ${selectedWeekOffset} недели`}
            </span>
            <button 
              className="week-nav-btn neo-raised"
              onClick={() => setSelectedWeekOffset(selectedWeekOffset + 1)}
            >
              <Icon name="chevron-right" size={20} />
            </button>
          </div>
        </div>
      )}

      {/* Month Selector */}
      {period === 'month' && (
        <div className="section">
          <div className="month-selector">
            <button 
              className="month-nav-btn neo-raised"
              onClick={() => {
                if (selectedMonth === 1) {
                  setSelectedMonth(12);
                  setSelectedYear(selectedYear - 1);
                } else {
                  setSelectedMonth(selectedMonth - 1);
                }
              }}
            >
              <Icon name="chevron-left" size={20} />
            </button>
            <div className="month-year-picker">
              <select 
                className="month-select"
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
              >
                {['Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь', 
                  'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь'].map((month, i) => (
                  <option key={i + 1} value={i + 1}>{month}</option>
                ))}
              </select>
              <select 
                className="year-select"
                value={selectedYear}
                onChange={(e) => setSelectedYear(parseInt(e.target.value))}
              >
                {Array.from({ length: 10 }, (_, i) => new Date().getFullYear() - i).map(year => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
            </div>
            <button 
              className="month-nav-btn neo-raised"
              onClick={() => {
                if (selectedMonth === 12) {
                  setSelectedMonth(1);
                  setSelectedYear(selectedYear + 1);
                } else {
                  setSelectedMonth(selectedMonth + 1);
                }
              }}
            >
              <Icon name="chevron-right" size={20} />
            </button>
          </div>
        </div>
      )}

      {/* Year Selector */}
      {period === 'year' && (
        <div className="section">
          <div className="year-selector">
            <button 
              className="year-nav-btn neo-raised"
              onClick={() => setSelectedYear(selectedYear - 1)}
            >
              <Icon name="chevron-left" size={20} />
            </button>
            <select 
              className="year-select-large"
              value={selectedYear}
              onChange={(e) => setSelectedYear(parseInt(e.target.value))}
            >
              {Array.from({ length: 20 }, (_, i) => new Date().getFullYear() - i).map(year => (
                <option key={year} value={year}>{year} год</option>
              ))}
            </select>
            <button 
              className="year-nav-btn neo-raised"
              onClick={() => setSelectedYear(selectedYear + 1)}
            >
              <Icon name="chevron-right" size={20} />
            </button>
          </div>
        </div>
      )}

      {/* Key Metrics */}
      <div className="section">
        <h3 className="section__title">{t('analytics.keyMetrics')}</h3>
        <div className="metrics-grid">
          <Card className="metric-card neo-raised neo-static">
            <div className="metric-card__icon metric-card__icon--income">
              <Icon name="trending-up" size={24} />
            </div>
            <div className="metric-card__content">
              <span className="metric-card__label">{t('analytics.metrics.income')}</span>
              <span className="metric-card__value metric-card__value--income">
                {formatCurrency(metrics.income, settings?.currency)}
              </span>
            </div>
          </Card>

          <Card className="metric-card neo-raised neo-static">
            <div className="metric-card__icon metric-card__icon--expense">
              <Icon name="trending-down" size={24} />
            </div>
            <div className="metric-card__content">
              <span className="metric-card__label">{t('analytics.metrics.expense')}</span>
              <span className="metric-card__value metric-card__value--expense">
                {formatCurrency(metrics.expense, settings?.currency)}
              </span>
            </div>
          </Card>

          <Card className="metric-card neo-raised neo-static">
            <div className="metric-card__icon metric-card__icon--balance">
              <Icon name="wallet" size={24} />
            </div>
            <div className="metric-card__content">
              <span className="metric-card__label">{t('analytics.metrics.balance')}</span>
              <span className={`metric-card__value ${metrics.balance >= 0 ? 'metric-card__value--income' : 'metric-card__value--expense'}`}>
                {formatCurrency(metrics.balance, settings?.currency)}
              </span>
            </div>
          </Card>

          <Card className="metric-card neo-raised neo-static">
            <div className="metric-card__icon metric-card__icon--daily">
              <Icon name="calendar" size={24} />
            </div>
            <div className="metric-card__content">
              <span className="metric-card__label">{t('analytics.metrics.avgDaily')}</span>
              <span className="metric-card__value">
                {formatCurrency(metrics.avgDailyExpense, settings?.currency)}
              </span>
            </div>
          </Card>

          <Card className="metric-card neo-raised neo-static">
            <div className="metric-card__icon metric-card__icon--count">
              <Icon name="list" size={24} />
            </div>
            <div className="metric-card__content">
              <span className="metric-card__label">{t('analytics.metrics.transactions')}</span>
              <span className="metric-card__value">{metrics.transactionsCount}</span>
            </div>
          </Card>

          <Card className="metric-card neo-raised neo-static">
            <div className="metric-card__icon metric-card__icon--savings">
              <Icon name="trophy" size={24} />
            </div>
            <div className="metric-card__content">
              <span className="metric-card__label">{t('analytics.metrics.savingsRate')}</span>
              <span className="metric-card__value">
                {metrics.income > 0 ? ((metrics.balance / metrics.income) * 100).toFixed(1) : 0}%
              </span>
            </div>
          </Card>
        </div>
      </div>

      {/* Spending Chart */}
      <div className="section">
        <h3 className="section__title">{t('analytics.spendingTrend')}</h3>
        <Card className="chart-card neo-raised neo-static">
          {metrics.expensesByDay.size > 0 ? (
            <div className="bar-chart">
              {(() => {
                // Берем только отображаемые данные
                const displayedData = Array.from(metrics.expensesByDay.entries()).slice(0, 7);
                // Находим максимум среди отображаемых данных
                const maxAmount = displayedData.length > 0 
                  ? Math.max(...displayedData.map(([_, amount]) => amount))
                  : 1;
                
                return displayedData.map(([day, amount], index) => {
                  // Минимальная высота 5%, чтобы столбик был виден даже при малых значениях
                  const calculatedHeight = maxAmount > 0 ? (amount / maxAmount) * 100 : 0;
                  const height = Math.max(calculatedHeight, amount > 0 ? 5 : 0);
                  
                  return (
                    <div key={index} className="bar-chart__item">
                      <div className="bar-chart__bar-container">
                        <div 
                          className="bar-chart__bar" 
                          style={{ height: `${height}%` }}
                        >
                          <div className="bar-chart__value">{formatCurrency(amount, settings?.currency, true)}</div>
                        </div>
                      </div>
                      <div className="bar-chart__label">{day}</div>
                    </div>
                  );
                });
              })()}
            </div>
          ) : (
            <div style={{ 
              padding: 'var(--spacing-xl)', 
              textAlign: 'center', 
              color: 'var(--color-text-secondary)' 
            }}>
              {t('analytics.noExpensesData') || 'Нет данных о расходах за выбранный период'}
            </div>
          )}
        </Card>
      </div>

      {/* Category Expenses */}
      {displayCategoryExpenses.length > 0 && (
        <div className="section">
          <div className="section__header">
            <h3 className="section__title">{t('analytics.categoryExpenses')}</h3>
            <div className="category-view-switcher">
              <button 
                className={`category-view-btn ${categoryView === 'list' ? 'neo-inset active' : 'neo-raised'}`}
                onClick={() => {
                  setCategoryView('list');
                  setSelectedPieSlice(null);
                }}
                title="Список"
              >
                <Icon name="list" size={18} />
              </button>
              <button 
                className={`category-view-btn ${categoryView === 'pie' ? 'neo-inset active' : 'neo-raised'}`}
                onClick={() => {
                  setCategoryView('pie');
                  setSelectedPieSlice(null);
                }}
                title="Круговая диаграмма"
              >
                <Icon name="circle" size={18} />
              </button>
              <button 
                className={`category-view-btn ${categoryView === 'bar' ? 'neo-inset active' : 'neo-raised'}`}
                onClick={() => {
                  setCategoryView('bar');
                  setSelectedPieSlice(null);
                }}
                title="Столбчатая диаграмма"
              >
                <Icon name="bar-chart" size={18} />
              </button>
            </div>
          </div>

          <Card className="category-expenses neo-raised neo-static">
            {categoryView === 'list' && (
              <div className="category-list-view">
                {displayCategoryExpenses.map((cat, index) => (
                  <div key={index} className="category-expense-item">
                    <div className="category-expense-item__left">
                      <div className="category-expense-item__icon" style={{ backgroundColor: getCategoryColor(index) }}>
                        <Icon name="folder" size={20} />
                      </div>
                      <div className="category-expense-item__info">
                        <span className="category-expense-item__name">{stripEmojis(cat.category)}</span>
                        <span className="category-expense-item__percent">{cat.percentage?.toFixed(1)}%</span>
                      </div>
                    </div>
                    <div className="category-expense-item__right">
                      <span className="category-expense-item__amount">
                        {formatCurrency(cat.amount, settings?.currency)}
                      </span>
                      <div className="category-expense-item__bar-container">
                        <div 
                          className="category-expense-item__bar" 
                          style={{ 
                            width: `${cat.percentage}%`,
                            backgroundColor: getCategoryColor(index)
                          }}
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {categoryView === 'pie' && (
              <div className="category-pie-view">
                <svg 
                  className="pie-chart" 
                  viewBox="0 0 200 200"
                  style={{ pointerEvents: 'all' }}
                >
                  <defs>
                    {displayCategoryExpenses.map((cat, index) => (
                      <radialGradient 
                        key={`gradient-${index}`} 
                        id={`pie-gradient-${index}`}
                        cx="40%" 
                        cy="40%"
                      >
                        <stop offset="0%" stopColor={getCategoryColor(index)} stopOpacity="1" />
                        <stop offset="100%" stopColor={getCategoryColor(index)} stopOpacity="0.85" />
                      </radialGradient>
                    ))}
                  </defs>
                  {(() => {
                    let currentAngle = -90; // Начинаем сверху
                    const slicesData = displayCategoryExpenses.map((cat, index) => {
                      const percentage = cat.percentage || 0;
                      const angle = (percentage / 100) * 360;
                      const startAngle = currentAngle;
                      const endAngle = currentAngle + angle;
                      currentAngle = endAngle;

                      const midAngle = (startAngle + endAngle) / 2;
                      const midRad = (midAngle * Math.PI) / 180;
                      const isSelected = selectedPieSlice === cat.category;

                      return {
                        cat,
                        index,
                        startAngle,
                        endAngle,
                        midAngle,
                        midRad,
                        isSelected,
                        angle
                      };
                    });

                    return slicesData.map(({ cat, index, startAngle, endAngle, midAngle, midRad, isSelected, angle }) => {
                      // Преобразование углов в радианы
                      const startRad = (startAngle * Math.PI) / 180;
                      const endRad = (endAngle * Math.PI) / 180;

                      // Координаты для SVG path
                      const x1 = 100 + 80 * Math.cos(startRad);
                      const y1 = 100 + 80 * Math.sin(startRad);
                      const x2 = 100 + 80 * Math.cos(endRad);
                      const y2 = 100 + 80 * Math.sin(endRad);

                      const largeArcFlag = angle > 180 ? 1 : 0;

                      const pathData = [
                        `M 100 100`,
                        `L ${x1} ${y1}`,
                        `A 80 80 0 ${largeArcFlag} 1 ${x2} ${y2}`,
                        'Z'
                      ].join(' ');

                      const offsetDistance = isSelected ? 10 : 0;
                      const offsetX = offsetDistance * Math.cos(midRad);
                      const offsetY = offsetDistance * Math.sin(midRad);
                      const scale = isSelected ? 1.08 : 1;

                      return (
                        <g 
                          key={`slice-${index}`}
                          style={{ 
                            cursor: 'pointer',
                            transformOrigin: '100px 100px',
                            transform: `translate(${offsetX}px, ${offsetY}px) scale(${scale})`,
                            transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                          }}
                          onClick={() => {
                            console.log('🥧 Pie slice clicked:', cat.category, 'isSelected:', isSelected);
                            setSelectedPieSlice(isSelected ? null : cat.category);
                          }}
                        >
                          <path
                            d={pathData}
                            fill={`url(#pie-gradient-${index})`}
                            className={`pie-slice ${isSelected ? 'pie-slice--selected' : ''}`}
                          />
                        </g>
                      );
                    });
                  })()}
                </svg>

                <div className="pie-legend">
                  {displayCategoryExpenses.map((cat, index) => (
                    <div 
                      key={index} 
                      className={`pie-legend-item ${selectedPieSlice === cat.category ? 'pie-legend-item--selected' : ''}`}
                      onClick={() => setSelectedPieSlice(selectedPieSlice === cat.category ? null : cat.category)}
                      style={{ cursor: 'pointer' }}
                    >
                      <div 
                        className="pie-legend-color" 
                        style={{ backgroundColor: getCategoryColor(index) }}
                      />
                      <div className="pie-legend-info">
                        <span className="pie-legend-name">{stripEmojis(cat.category)}</span>
                        <span className="pie-legend-value">
                          {formatCurrency(cat.amount, settings?.currency)} ({cat.percentage?.toFixed(1)}%)
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {categoryView === 'bar' && (
              <div className="category-bar-view">
                {displayCategoryExpenses.map((cat, index) => {
                  const maxAmount = Math.max(...displayCategoryExpenses.map(c => c.amount));
                  const heightPercent = (cat.amount / maxAmount) * 100;

                  return (
                    <div key={index} className="category-bar-item">
                      <div className="category-bar-container">
                        <div 
                          className="category-bar-fill" 
                          style={{ 
                            height: `${Math.max(heightPercent, 5)}%`,
                            backgroundColor: getCategoryColor(index)
                          }}
                        >
                          <div className="category-bar-value">
                            {formatCurrency(cat.amount, settings?.currency, true)}
                          </div>
                        </div>
                      </div>
                      <div className="category-bar-label">
                        {stripEmojis(cat.category).split(' ').slice(0, 2).join(' ')}
                      </div>
                      <div className="category-bar-percent">{cat.percentage?.toFixed(1)}%</div>
                    </div>
                  );
                })}
              </div>
            )}
          </Card>
        </div>
      )}

      {/* Budget Health */}
      <div className="section">
        <h3 className="section__title">{t('analytics.financialHealth')}</h3>
        <div className="health-cards">
          <Card className="health-card neo-raised neo-static">
            <div className="health-card__header">
              <Icon name="target" size={24} />
              <span className="health-card__title">{t('analytics.health.budgetControl')}</span>
            </div>
            <div className="health-card__score">
              <div className="health-card__score-value">{calculateBudgetScore()}</div>
              <div className="health-card__score-label">{t('analytics.health.outOf')}</div>
            </div>
            <div className="health-card__progress">
              <div className="health-card__progress-bar" style={{ width: `${calculateBudgetScore()}%` }} />
            </div>
            <div className="health-card__hint">
              {(() => {
                if (metrics.income === 0) return 'Добавьте доходы для оценки';
                const savingsRate = ((metrics.balance / metrics.income) * 100).toFixed(0);
                return `Сбережения: ${savingsRate}% от дохода`;
              })()}
            </div>
          </Card>

          <Card className="health-card neo-raised neo-static">
            <div className="health-card__header">
              <Icon name="award" size={24} />
              <span className="health-card__title">{t('analytics.health.trackingRegularity')}</span>
            </div>
            <div className="health-card__score">
              <div className="health-card__score-value">{calculateTrackingScore()}</div>
              <div className="health-card__score-label">{t('analytics.health.outOf')}</div>
            </div>
            <div className="health-card__progress">
              <div className="health-card__progress-bar" style={{ width: `${calculateTrackingScore()}%` }} />
            </div>
            <div className="health-card__hint">
              {(() => {
                const uniqueDays = new Set(
                  transactions
                    .filter(t => {
                      const tDate = new Date(t.date);
                      if (period === 'today') {
                        return tDate.toDateString() === new Date().toDateString();
                      } else if (period === 'week') {
                        const weekStart = new Date();
                        weekStart.setDate(weekStart.getDate() - weekStart.getDay() + (selectedWeekOffset * 7));
                        const weekEnd = new Date(weekStart);
                        weekEnd.setDate(weekEnd.getDate() + 6);
                        return tDate >= weekStart && tDate <= weekEnd;
                      } else if (period === 'month') {
                        return tDate.getFullYear() === selectedYear && tDate.getMonth() === selectedMonth - 1;
                      } else if (period === 'year') {
                        return tDate.getFullYear() === selectedYear;
                      }
                      return false;
                    })
                    .map(t => new Date(t.date).toDateString())
                ).size;
                
                let daysInPeriod = 1;
                if (period === 'today') daysInPeriod = 1;
                else if (period === 'week') daysInPeriod = 7;
                else if (period === 'month') daysInPeriod = new Date(selectedYear, selectedMonth, 0).getDate();
                else if (period === 'year') daysInPeriod = 365;
                
                return `Активность: ${uniqueDays} из ${daysInPeriod} дней`;
              })()}
            </div>
          </Card>
        </div>
      </div>

      {/* Insight Modal */}
      {selectedInsight && (
        <div className="insight-modal-overlay" onClick={() => setSelectedInsight(null)}>
          <div className="insight-modal" onClick={(e) => e.stopPropagation()}>
            <div className="insight-modal__header">
              <div className="insight-modal__icon">
                <Icon name="lightbulb" size={24} />
              </div>
              <button className="insight-modal__close" onClick={() => setSelectedInsight(null)}>
                <Icon name="close" size={24} />
              </button>
            </div>
            <div className="insight-modal__content">
              <h3 className="insight-modal__title">{t('analytics.insight')}</h3>
              <p className="insight-modal__text">
                {selectedInsight.text || selectedInsight.message || ''}
              </p>
            </div>
          </div>
        </div>
      )}
      </div>
    </PremiumFeature>
  );

  function calculateBudgetScore() {
    // Если нет дохода, но есть расходы - плохой контроль
    if (metrics.income === 0) {
      return metrics.expense > 0 ? 10 : 50; // Если нет транзакций вообще - средний балл
    }
    
    // Процент сбережений = (доход - расходы) / доход * 100
    const savingsRate = (metrics.balance / metrics.income) * 100;
    
    // Оценка контроля бюджета
    if (savingsRate >= 30) return 100;  // Откладываете 30%+ = отлично
    if (savingsRate >= 20) return 90;   // 20%+ = очень хорошо
    if (savingsRate >= 15) return 80;   // 15%+ = хорошо
    if (savingsRate >= 10) return 70;   // 10%+ = неплохо
    if (savingsRate >= 5) return 60;    // 5%+ = средне
    if (savingsRate >= 0) return 50;    // Сводите концы с концами
    if (savingsRate >= -10) return 40;  // Небольшой перерасход
    if (savingsRate >= -20) return 30;  // Перерасход
    if (savingsRate >= -30) return 20;  // Сильный перерасход
    return 10; // Критический перерасход
  }

  function calculateTrackingScore() {
    // Подсчитываем уникальные дни с транзакциями
    const uniqueDays = new Set(
      transactions
        .filter(t => {
          const tDate = new Date(t.date);
          if (period === 'today') {
            return tDate.toDateString() === new Date().toDateString();
          } else if (period === 'week') {
            const weekStart = new Date();
            weekStart.setDate(weekStart.getDate() - weekStart.getDay() + (selectedWeekOffset * 7));
            const weekEnd = new Date(weekStart);
            weekEnd.setDate(weekEnd.getDate() + 6);
            return tDate >= weekStart && tDate <= weekEnd;
          } else if (period === 'month') {
            return tDate.getFullYear() === selectedYear && tDate.getMonth() === selectedMonth - 1;
          } else if (period === 'year') {
            return tDate.getFullYear() === selectedYear;
          }
          return false;
        })
        .map(t => new Date(t.date).toDateString())
    ).size;

    let daysInPeriod = 1;
    if (period === 'today') daysInPeriod = 1;
    else if (period === 'week') daysInPeriod = 7;
    else if (period === 'month') daysInPeriod = new Date(selectedYear, selectedMonth, 0).getDate();
    else if (period === 'year') daysInPeriod = 365;
    
    // Процент дней с активностью
    const activityRate = (uniqueDays / daysInPeriod) * 100;
    
    // Оценка основана на проценте дней с транзакциями
    if (activityRate >= 50) return 100; // 50%+ дней = отлично
    if (activityRate >= 35) return 90;  // 35%+ дней = очень хорошо
    if (activityRate >= 25) return 80;  // 25%+ дней = хорошо  
    if (activityRate >= 15) return 70;  // 15%+ дней = неплохо
    if (activityRate >= 10) return 60;  // 10%+ дней = средне
    if (activityRate >= 5) return 40;   // 5%+ дней = ниже среднего
    if (uniqueDays > 0) return 30;      // Хоть какая-то активность
    return 10; // Нет активности
  }
};

function getCategoryColor(index: number): string {
  const colors = [
    '#007AFF', // Синий
    '#34C759', // Зеленый
    '#FF9500', // Оранжевый
    '#FF3B30', // Красный
    '#AF52DE', // Фиолетовый
    '#FF2D55', // Розовый
    '#5856D6', // Индиго
    '#00C7BE', // Бирюзовый
  ];
  return colors[index % colors.length];
}
